/**
 * Admin · Appearance (super-admin) — white-label the software: app name, tagline, short
 * brand mark, accent color, and logo. The logo uploads straight to R2/CDN (presigned;
 * pixels never hit the API) with an inline data-URL fallback when hosting is off. Saving
 * pushes the fresh branding into the cache so the login screen + shell update everywhere.
 */
import { useCallback, useEffect, useState } from "react";
import { ActionBar } from "../ui/ActionBar.js";
import { useServices } from "../app/context.js";
import { InfoHint } from "../ui/Tooltip.js";
import { cacheEpoch, readCache, readCacheEntry, writeCache } from "../stores/cache.js";
import { isDurableRef } from "../app/refs.js";
import { CACHE_KEYS } from "../stores/sessionWarmup.js";
import { StaleNotice } from "./StaleNotice.js";
import type { Staleness } from "../stores/useCachedResource.js";
import type { Appearance } from "@genz/platform";
import { fetchWithTimeout, UPLOAD_TIMEOUT_MS } from "../templates/timeout.js";

const MIME_EXT: Record<string, string> = {
  "image/png": "png", "image/jpeg": "jpg", "image/webp": "webp", "image/svg+xml": "svg",
};

/**
 * The default swatch for the accent picker, sourced from the Aurora `--accent-strong`
 * token (so the picker never seeds a stale off-palette colour). An <input type="color">
 * needs a literal hex value, so we resolve the token at runtime and fall back to the
 * token's value only if CSS isn't available (SSR / tests).
 */
function defaultAccentHex(): string {
  if (typeof window !== "undefined" && typeof getComputedStyle === "function") {
    const v = getComputedStyle(document.documentElement).getPropertyValue("--accent-strong").trim();
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return v;
  }
  return "#2a4ad4"; // --accent-strong fallback (accent)
}

export function AppearanceTab() {
  const { client, io } = useServices();
  // Cache-first: seed from the cached branding (shared with the shell/login) so the
  // tab shows instantly on re-open; the fetch below revalidates quietly.
  const [a, setA] = useState<Appearance | null>(() => readCache<Appearance>(CACHE_KEYS.appearance));
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // GEN-8: a failed reload used to be dropped whenever branding was already cached,
  // so the tab could be editing a week-old copy while claiming to be current.
  const [stale, setStale] = useState<Staleness | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const epoch = cacheEpoch();
    try {
      const fresh = await client.getAppearance();
      writeCache(CACHE_KEYS.appearance, fresh, epoch);
      setA(fresh);
      setStale(null);
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      const cached = readCacheEntry<Appearance>(CACHE_KEYS.appearance);
      if (cached == null) setError(reason);
      else setStale({ cachedAt: cached.at, reason });
    }
  }, [client]);

  useEffect(() => { void load(); }, [load]);

  function set<K extends keyof Appearance>(key: K, value: Appearance[K]) {
    setA((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSaved(false);
  }

  async function pickLogo() {
    const files = await io.pickFiles({
      accept: ["image/png", "image/jpeg", "image/webp", "image/svg+xml"], multiple: false,
      maxBytes: 4 * 1024 * 1024,
    });
    if (!files.length) return;
    const f = files[0];
    const mime = f.mime ?? "image/png";
    const ext = MIME_EXT[mime] ?? "png";
    setUploading(true);
    setError(null);
    try {
      const target = await client.adminLogoUploadTarget({ ext });
      if (!/^https?:\/\//.test(target.upload_url)) throw new Error("hosting is local-only (no CDN target)");
      const res = await fetchWithTimeout(target.upload_url, {
        method: "PUT", headers: { "Content-Type": mime, ...(target.fields ?? {}) },
        body: f.bytes as BlobPart as BodyInit,
      }, UPLOAD_TIMEOUT_MS, "Logo upload");
      if (!res.ok) throw new Error(`upload PUT -> ${res.status}`);
      if (!isDurableRef(target.public_url)) throw new Error("hosting returned a non-durable public URL — check hosting_public_host");
      set("logo_url", target.public_url);
    } catch (e) {
      // No silent data-URL fallback: the whole-tenant logo persisted as a multi-MB base64
      // blob hid the broken hosting and bloated every appearance read. Surface it instead
      // (the cloud rejects non-http(s) logo_url with a 422 now anyway).
      setError(`Logo upload failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    if (!a) return;
    setBusy(true);
    setError(null);
    const epoch = cacheEpoch();
    try {
      const updated = await client.updateAppearance({
        app_name: a.app_name, tagline: a.tagline, short_mark: a.short_mark,
        accent_color: a.accent_color ?? "", logo_url: a.logo_url ?? "",
      });
      setA(updated);
      writeCache(CACHE_KEYS.appearance, updated, epoch); // live-update the shell/login branding
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!a) return <p className="faint" data-testid="appearance-loading">Loading…</p>;

  const accentHex = a.accent_color || defaultAccentHex();

  return (
    <section className="admin-tab" data-testid="admin-appearance">
      <div className="admin-panel admin-panel--appearance">
        <div className="admin-panel__head admin-panel__head--flush">
          <div>
            <strong className="admin-panel__title panel-title">White-label appearance <InfoHint text="Name, logo and accent shown on the login screen and in-app." /></strong>
          </div>
        </div>
        {error && <p role="alert" data-testid="appearance-error">{error}</p>}
        <StaleNotice stale={stale} testid="appearance-stale" />


        <div data-testid="appearance-form">
          {/* App name — compact hairline row, 240px input (mock parity). */}
          <div className="setting-row">
            <div>
              <span className="setting-row__label">App name</span>
              <span className="setting-row__hint">Login · sidebar · title</span>
            </div>
            <div className="setting-row__control">
              <input type="text" data-testid="app-name" value={a.app_name}
                onChange={(e) => set("app_name", e.target.value)} />
            </div>
          </div>

          {/* Tagline (app extra) */}
          <div className="setting-row">
            <div>
              <span className="setting-row__label">Tagline</span>
              <span className="setting-row__hint">Shown under the name on login</span>
            </div>
            <div className="setting-row__control">
              <input type="text" data-testid="app-tagline" value={a.tagline}
                onChange={(e) => set("tagline", e.target.value)} />
            </div>
          </div>

          {/* Accent color — 34px rounded swatch + ~110px mono hex input (mock parity). */}
          <div className="setting-row">
            <div>
              <span className="setting-row__label">Accent color</span>
              <span className="setting-row__hint">Drives buttons, active nav, focus</span>
            </div>
            <div className="setting-row__control">
              <input type="color" data-testid="app-accent" className="appearance-swatch"
                value={accentHex}
                aria-label="Accent color"
                onChange={(e) => set("accent_color", e.target.value)} />
              <input type="text" data-testid="app-accent-hex" className="appearance-hex"
                value={accentHex}
                aria-label="Accent color hex"
                onChange={(e) => set("accent_color", e.target.value)} />
            </div>
          </div>

          {/* Short mark (app extra — fallback badge text) */}
          <div className="setting-row">
            <div>
              <span className="setting-row__label">Short mark</span>
              <span className="setting-row__hint">Fallback badge text</span>
            </div>
            <div className="setting-row__control">
              <input type="text" maxLength={8} data-testid="app-mark" value={a.short_mark}
                onChange={(e) => set("short_mark", e.target.value)} />
            </div>
          </div>

          {/* Logo — 38px rounded brand badge + Upload logo (mock parity). */}
          <div className="setting-row" data-testid="appearance-logo">
            <div>
              <span className="setting-row__label">Logo</span>
              <span className="setting-row__hint">PNG / SVG · hosted on R2/CDN</span>
            </div>
            <div className="setting-row__control">
              {a.logo_url
                ? <img src={a.logo_url} alt="logo" className="appearance-logo-mark" data-testid="logo-preview" />
                : <span className="appearance-logo-mark appearance-logo-mark--empty" aria-hidden="true">{(a.short_mark || a.app_name || "G").charAt(0).toUpperCase()}</span>}
              <button type="button" className="btn btn--sm btn--ghost" disabled={uploading}
                data-testid="logo-pick" onClick={() => void pickLogo()}>{uploading ? "Uploading…" : "Upload logo"}</button>
              {a.logo_url && (
                <button type="button" className="btn btn--sm btn--ghost" data-testid="logo-clear"
                  onClick={() => set("logo_url", null)}>Remove</button>
              )}
            </div>
          </div>
        </div>

        {/* Rule 7 — the commit at the bottom of the column, in the SHARED bar.
            `.settings__actions` is the customer Settings page's action row; borrowing it here
            was the same cross-feature reach owner Rule B closes on Workspace defaults. */}
        <ActionBar
          label="Appearance actions"
          primary={
            <button type="button" className="btn btn--primary" disabled={busy}
              data-testid="appearance-save" onClick={() => void save()}>
              {busy ? "Saving…" : "Save"}
            </button>
          }
        >
          {saved && <span className="faint" data-testid="appearance-saved">Saved.</span>}
        </ActionBar>
      </div>
    </section>
  );
}
